import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export interface KeyboardShortcut {
    key: string;
    action: string;
}

export interface HelpDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    keyboardShortcuts?: KeyboardShortcut[];
}

export const HelpDialog: React.FC<HelpDialogProps> = ({
    open,
    onOpenChange,
    keyboardShortcuts = []
}) => {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
                <DialogHeader className="mb-1">
                    <DialogTitle className="text-lg">Timeline Help</DialogTitle>
                    <DialogDescription className="text-sm">
                        Learn how to use the timeline feature effectively.
                    </DialogDescription>
                </DialogHeader>

                <Tabs defaultValue="setup">
                    <TabsList className="grid grid-cols-4 mb-5">
                        <TabsTrigger value="setup">Setup</TabsTrigger>
                        <TabsTrigger value="basics">Basics</TabsTrigger>
                        <TabsTrigger value="advanced">Advanced</TabsTrigger>
                        <TabsTrigger value="shortcuts">Keyboard Shortcuts</TabsTrigger>
                    </TabsList>

                    <TabsContent value="setup" className="space-y-5">
                        <section>
                            <h3 className="text-base font-medium mb-2">Timeline Setup Guide</h3>
                            <p className="text-sm text-muted-foreground mb-4">
                                Before you can use the timeline, you need to complete two simple setup steps.
                            </p>

                            <div className="space-y-4">
                                <div className="border rounded-md p-4 bg-background">
                                    <h4 className="text-sm font-medium mb-2 flex items-center">
                                        <div className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center mr-2 text-xs">1</div>
                                        Step 1: Create a Timeline
                                    </h4>
                                    <p className="text-sm text-muted-foreground mb-3">
                                        A timeline represents a story thread or character arc in your narrative.
                                    </p>
                                    <div className="bg-muted/30 rounded p-3 text-sm">
                                        <strong>How to create:</strong>
                                        <ul className="list-disc pl-5 text-sm text-muted-foreground space-y-1 mt-1">
                                            <li>Click the "Timelines: Add" button in the toolbar</li>
                                            <li>Enter a title for your timeline (e.g., "Main Plot", "Character Arc")</li>
                                            <li>Optionally select a storytelling template</li>
                                            <li>Click "Create Timeline"</li>
                                        </ul>
                                    </div>
                                </div>

                                <div className="border rounded-md p-4 bg-background">
                                    <h4 className="text-sm font-medium mb-2 flex items-center">
                                        <div className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center mr-2 text-xs">2</div>
                                        Step 2: Add Chapters
                                    </h4>
                                    <p className="text-sm text-muted-foreground mb-3">
                                        Chapters organize your story into sections, acts, or time periods.
                                    </p>
                                    <div className="bg-muted/30 rounded p-3 text-sm">
                                        <strong>How to create:</strong>
                                        <ul className="list-disc pl-5 text-sm text-muted-foreground space-y-1 mt-1">
                                            <li>Click the "Chapters: New" button in the toolbar</li>
                                            <li>Enter a name for your chapter (e.g., "Act 1", "Introduction")</li>
                                            <li>Click "Save"</li>
                                            <li>Repeat to add more chapters as needed</li>
                                        </ul>
                                    </div>
                                </div>

                                <div className="border rounded-md p-4 bg-background">
                                    <h4 className="text-sm font-medium mb-2 flex items-center">
                                        <div className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center mr-2 text-xs">✓</div>
                                        Step 3: Start Using Your Timeline
                                    </h4>
                                    <p className="text-sm text-muted-foreground mb-3">
                                        Once you've created at least one timeline and one chapter, your timeline grid will appear automatically.
                                    </p>
                                    <div className="bg-muted/30 rounded p-3 text-sm">
                                        <strong>What's next:</strong>
                                        <ul className="list-disc pl-5 text-sm text-muted-foreground space-y-1 mt-1">
                                            <li>Click in any cell to add a plot card</li>
                                            <li>Add more timelines or chapters as your story develops</li>
                                            <li>Use the toolbar options to customize your view</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </section>
                    </TabsContent>

                    <TabsContent value="basics" className="space-y-5">
                        <section>
                            <h3 className="text-base font-medium mb-2">Getting Started</h3>
                            <p className="text-sm text-muted-foreground mb-4">
                                The Timeline view helps you organize your story's plot lines and chapters visually.
                            </p>

                            <div className="bg-muted/30 border rounded-md p-4 mb-5">
                                <h4 className="text-sm font-medium mb-2 flex items-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
                                    Timeline Setup Requirements
                                </h4>
                                <p className="text-sm text-muted-foreground mb-2">
                                    To use the timeline feature, you need to set up two components:
                                </p>
                                <ol className="list-decimal pl-5 text-sm text-muted-foreground space-y-1.5">
                                    <li><strong>Create a Timeline:</strong> This establishes the structure for your story visualization.</li>
                                    <li><strong>Add Chapters:</strong> These organize your story into sections or acts.</li>
                                </ol>
                                <p className="text-sm text-muted-foreground mt-2">
                                    Once both components are created, your timeline grid will appear, and you can start adding plot cards.
                                </p>
                            </div>

                            <h4 className="text-sm font-medium mb-2">Basic Concepts:</h4>
                            <ul className="list-disc pl-5 text-sm text-muted-foreground space-y-1.5 mb-4">
                                <li><strong>Timelines/Plot Lines:</strong> Horizontal rows representing different story threads or character arcs.</li>
                                <li><strong>Chapters:</strong> Vertical columns representing progression of your story.</li>
                                <li><strong>Cards:</strong> Individual plot points, events, or notes placed at the intersection of timelines and chapters.</li>
                            </ul>

                            <h4 className="text-sm font-medium mb-2">Basic Actions:</h4>
                            <ul className="list-disc pl-5 text-sm text-muted-foreground space-y-1.5">
                                <li>Click the "+" buttons to add new timelines or chapters.</li>
                                <li>Click in a cell to add a new card at that intersection.</li>
                                <li>Click on an existing card to view or edit it.</li>
                                <li>Drag cards to reorder or move them between chapters and timelines.</li>
                            </ul>
                        </section>
                    </TabsContent>

                    <TabsContent value="advanced" className="space-y-5">
                        <section>
                            <h3 className="text-base font-medium mb-2">Advanced Features</h3>

                            <h4 className="text-sm font-medium mb-2">Card Management:</h4>
                            <ul className="list-disc pl-5 text-sm text-muted-foreground space-y-1.5 mb-4">
                                <li><strong>Multi-select:</strong> Hold Shift while clicking to select multiple cards.</li>
                                <li><strong>Bulk Move:</strong> Select multiple cards, then right-click and choose "Move" to relocate them together.</li>
                                <li><strong>Bulk Delete:</strong> Select multiple cards and press Delete to remove them all.</li>
                            </ul>

                            <h4 className="text-sm font-medium mb-2">Timeline Organization:</h4>
                            <ul className="list-disc pl-5 text-sm text-muted-foreground space-y-1.5 mb-4">
                                <li><strong>Reordering:</strong> Use the "Order" buttons to drag and drop timelines or chapters into a new sequence.</li>
                                <li><strong>Compact Mode:</strong> Toggle compact view to show more content on screen at once.</li>
                                <li><strong>Zoom:</strong> Adjust zoom level to fit more content or focus on specific areas.</li>
                            </ul>

                            <h4 className="text-sm font-medium mb-2">Search and Filter:</h4>
                            <ul className="list-disc pl-5 text-sm text-muted-foreground space-y-1.5">
                                <li>Use the search box to quickly find cards by title or content.</li>
                                <li>Results will be highlighted across all timelines and chapters.</li>
                            </ul>

                            <h4 className="text-sm font-medium mb-2">Story Templates:</h4>
                            <ul className="list-disc pl-5 text-sm text-muted-foreground space-y-1.5 mb-4">
                                <li><strong>Create from Template:</strong> When creating a new timeline, choose from popular storytelling frameworks like Hero's Journey or Save the Cat.</li>
                                <li><strong>Apply Template:</strong> Add template story beats to an existing timeline using the Template button in the toolbar.</li>
                                <li><strong>Customize Content:</strong> Edit and adjust template-generated content to fit your specific story needs.</li>
                            </ul>
                        </section>
                    </TabsContent>

                    <TabsContent value="shortcuts" className="space-y-5">
                        <section>
                            <h3 className="text-base font-medium mb-2">Keyboard Shortcuts</h3>
                            <p className="text-sm text-muted-foreground mb-4">
                                These shortcuts help you work more efficiently with the timeline.
                            </p>

                            <div className="overflow-hidden border rounded-md">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="bg-muted border-b border-border">
                                            <th className="text-left py-2 px-4 font-medium">Key</th>
                                            <th className="text-left py-2 px-4 font-medium">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {keyboardShortcuts.length > 0 ? (
                                            keyboardShortcuts.map((shortcut, index) => (
                                                <tr key={index} className={index % 2 === 0 ? 'bg-background' : 'bg-muted/30'}>
                                                    <td className="py-2 px-4">
                                                        <kbd className="px-1.5 py-0.5 rounded text-xs font-mono bg-muted border border-border">
                                                            {shortcut.key}
                                                        </kbd>
                                                    </td>
                                                    <td className="py-2 px-4">{shortcut.action}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <>
                                                <tr className="bg-background">
                                                    <td className="py-2 px-4">
                                                        <kbd className="px-1.5 py-0.5 rounded text-xs font-mono bg-muted border border-border">
                                                            Ctrl+M
                                                        </kbd>
                                                    </td>
                                                    <td className="py-2 px-4">Toggle compact mode</td>
                                                </tr>
                                                <tr className="bg-muted/30">
                                                    <td className="py-2 px-4">
                                                        <kbd className="px-1.5 py-0.5 rounded text-xs font-mono bg-muted border border-border">
                                                            Delete
                                                        </kbd>
                                                    </td>
                                                    <td className="py-2 px-4">Delete selected cards</td>
                                                </tr>
                                                <tr className="bg-background">
                                                    <td className="py-2 px-4">
                                                        <kbd className="px-1.5 py-0.5 rounded text-xs font-mono bg-muted border border-border">
                                                            F1
                                                        </kbd>
                                                        {" or "}
                                                        <kbd className="px-1.5 py-0.5 rounded text-xs font-mono bg-muted border border-border">
                                                            Ctrl+H
                                                        </kbd>
                                                    </td>
                                                    <td className="py-2 px-4">Open this help dialog</td>
                                                </tr>
                                                <tr className="bg-muted/30">
                                                    <td className="py-2 px-4">
                                                        <kbd className="px-1.5 py-0.5 rounded text-xs font-mono bg-muted border border-border">
                                                            Escape
                                                        </kbd>
                                                    </td>
                                                    <td className="py-2 px-4">Close dialogs or clear selection</td>
                                                </tr>
                                                <tr className="bg-background">
                                                    <td className="py-2 px-4">
                                                        <kbd className="px-1.5 py-0.5 rounded text-xs font-mono bg-muted border border-border">
                                                            Ctrl+T
                                                        </kbd>
                                                    </td>
                                                    <td className="py-2 px-4">Apply story template</td>
                                                </tr>
                                                <tr className="bg-muted/30">
                                                    <td className="py-2 px-4">
                                                        <kbd className="px-1.5 py-0.5 rounded text-xs font-mono bg-muted border border-border">
                                                            Ctrl+Shift+N
                                                        </kbd>
                                                    </td>
                                                    <td className="py-2 px-4">Create new timeline</td>
                                                </tr>
                                            </>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    </TabsContent>
                </Tabs>
            </DialogContent>
        </Dialog>
    );
};